import {ref, type Ref} from 'vue';
import {useRouter} from 'vue-router';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useSignOut} from '@/ts/composables/stateful/use-auth';
import {persistentStorage} from '@/ts/instances/persistant-storage';
import {sessionStorage} from '@/ts/instances/session-storage';
import {pagesPath} from '@/ts/router/pages-path';
import {ErrorCodesDto} from '@/ts/types/dto/error-codes-dto';
import type {CompleteSignUpRequest} from '@/ts/types/dto/sign-in-dto';
import {AppError} from '@/ts/utils/errors';

/** What the sign-up page needs from the shortened, already-authenticated path. */
export interface UseCompleteSignUp {
  /** Whether this visit runs the shortened wizard for an already-authenticated identity. */
  isActive: Ref<boolean>;
  /** Creates the Balm account for the authenticated Zitadel identity. */
  submit(payload: CompleteSignUpRequest): Promise<void>;
  /** Whether a failed submit was one this path handles itself, so the caller must not rethrow. */
  absorbError(err: unknown): Promise<boolean>;
  /** Where the user goes once the submit resolved: into the app, or out of a spent attempt. */
  finish(): void;
}

/**
 * The Google half of sign-up (IPD-2844): an identity that already exists in Zitadel, so there is
 * no email or password to collect, the session already proves a human, and only the Balm side of
 * the account is left to create.
 */
export function useCompleteSignUp(): UseCompleteSignUp {
  const api = useApi();
  const router = useRouter();
  const signOut = useSignOut();

  // The signal from continue-auth is consumed on arrival and captured here, so it applies to this
  // visit only. Leaving it in sessionStorage made it sticky for the whole tab: reloading
  // /public/sign-up, or typing the URL, then dropped the user mid-form on Personalize with no way
  // back to the start. Now a fresh load always shows the first screen, and clicking Google again
  // re-enters the short flow. The token is required too — the flag alone could outlive a sign-out.
  const isActive = ref(sessionStorage.completeSignUp && Boolean(persistentStorage.auth));
  sessionStorage.completeSignUp = false;

  // The code that ended this attempt, when it ended it: the Balm account cannot be created for this
  // identity, so the only way on is a fresh sign-in. Null while the attempt is still viable.
  const exitReason = ref<ErrorCodesDto | null>(null);

  async function submit(payload: CompleteSignUpRequest): Promise<void> {
    exitReason.value = null;
    // The account already exists in Zitadel; the BE takes the email from the token
    // and only creates the Balm side (DB row, consents, contract).
    await api.signUp.completeSignUp(payload);
  }

  /**
   * Whether the account this attempt could not create exists anyway.
   *
   * `conflict` is the generic 409 of every endpoint and, on this one, also whatever the auth
   * server answers through Feign, so it does not say by itself that the identity is now
   * registered. Ask the endpoint that knows. Failing closed on purpose, unlike the other probe
   * sites: they answer "registered" when the call fails because locking a registered user out is
   * the worse outcome there, while here the worse outcome is reporting success for a failure and
   * sending the user home with no account. The backend is giving the already-registered case a
   * code of its own; this check becomes that code.
   */
  async function isRegistered(): Promise<boolean> {
    const {registered} = await api.signUp.getRegistrationStatus().catch(() => ({registered: false}));
    return registered;
  }

  /**
   * complete-signup failures the caller must not report as an error, because a toast on the
   * consent step is not something the user can act on: the shortened wizard has no email step to
   * go back to and no way out except a reload.
   *
   * Three of them end the attempt for good, and `finish` walks the user out to a fresh sign-in:
   * - The token outlived its Zitadel user (an account deleted while still signed in). It shares
   *   its 404 with an unknown voucher, which is a real error the user has to see, so the code is
   *   what tells them apart and `resource_not_found` deliberately does NOT absorb.
   * - The identity's address was never verified, so the backend will not open an account for it.
   * - Another account already holds the address. Only a *different* account counts:
   *   `email_already_registered` means this identity still has none, so letting it through would
   *   401 straight back out.
   *
   * Two carry the opposite outcome — the account exists after all, so the user simply goes in:
   * - `user_already_registered` says exactly that, so it is taken at its word.
   * - `conflict` is the generic 409 of every endpoint and also whatever the auth server answers
   *   through Feign, so it cannot mean this on its own and is checked instead (see `isRegistered`).
   *   It is what a deployment without the code above still sends; once every environment serves it,
   *   this branch and the probe behind it can go.
   */
  async function absorbError(err: unknown): Promise<boolean> {
    if (!(err instanceof AppError)) {
      return false;
    }
    switch (err.statusCode) {
      case ErrorCodesDto.AUTH_SERVER_USER_NOT_FOUND:
      case ErrorCodesDto.AUTH_SERVER_EMAIL_UNVERIFIED:
      case ErrorCodesDto.EMAIL_ALREADY_REGISTERED:
        exitReason.value = err.statusCode;
        return true;
      // The account exists after all, so the only thing left is to let the user in: absorbed with
      // no exit reason, which is what sends `finish` into the app rather than back out to a fresh
      // sign-in. Said by the backend rather than asked for, so no probe.
      //
      // What they typed in this wizard is discarded, the backend having recorded nothing without a
      // successful persistSignup: name, birth year, country, consents and any voucher code. That is
      // accepted rather than overlooked. The account they are being let into already has its own,
      // and a voucher meant for it goes through the subscription flow, which is where a voucher
      // applied to an existing account belongs.
      case ErrorCodesDto.USER_ALREADY_REGISTERED:
        return true;
      case ErrorCodesDto.CONFLICT:
        return isRegistered();
      default:
        return false;
    }
  }

  function finish(): void {
    if (exitReason.value) {
      // Dropping our token is not enough: Zitadel still holds a session cookie for this identity
      // and would hand back a token for it, looping straight back here. End that session too and
      // land on sign-up logged out, so the next attempt authenticates for real. No backend logout
      // to skip past: this identity has no Balm account, so `/me/logout` would only 401.
      //
      // The reason travels with the redirect so the first screen can explain it — except for the
      // deleted account, which has nothing to explain: signing up again is the whole answer.
      const explain = exitReason.value === ErrorCodesDto.AUTH_SERVER_USER_NOT_FOUND ? null : exitReason.value;
      void signOut(pagesPath.public.signUp, {skipBeLogout: true, postLogoutState: explain});
      return;
    }
    // Already authenticated and now registered: no email to verify and no password to
    // set, so skip the verification dialog and go straight in — to where they were headed when
    // the sign-in interrupted them, if that is anywhere.
    const intent = sessionStorage.redirectIntent;
    sessionStorage.redirectIntent = null;
    void router.replace(intent?.startsWith('/') ? intent : pagesPath.main.home);
  }

  return {isActive, submit, absorbError, finish};
}
